// authService.js
// Authentication service for handling user login, registration, and Basic authentication

// Base API configuration
const API_BASE_URL = '/api';

// Helper function to build API URLs
const buildApiUrl = (endpoint) => `${API_BASE_URL}${endpoint}`;

// Default headers for all requests
const defaultHeaders = {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': '69420'
};

// Storage keys
const USER_KEY = 'auth_user';
const CREDENTIALS_KEY = 'auth_credentials';

// Request queue for retrying failed requests after re-authentication
let pendingRequests = [];
let isReauthenticating = false;

// Listen for pending requests check
if (typeof window !== 'undefined') {
    window.addEventListener('checkPendingRequests', () => {
        if (pendingRequests.length > 0) {
            window.dispatchEvent(new CustomEvent('pendingRequestsFound', { 
                detail: { count: pendingRequests.length } 
            }));
        }
    });
}

class AuthService {
    constructor() {
        this.user = JSON.parse(localStorage.getItem(USER_KEY) || 'null');
        
        // Try to restore credentials from sessionStorage (more secure than localStorage)
        const storedCredentials = sessionStorage.getItem(CREDENTIALS_KEY);
        if (storedCredentials) {
            try {
                const credentials = JSON.parse(storedCredentials);
                this.username = credentials.username;
                this.password = credentials.password;
            } catch (error) {
                console.warn('Failed to restore credentials:', error);
                this.username = null;
                this.password = null;
            }
        } else {
            this.username = null;
            this.password = null;
        }
    }
    
    // Get current user
    getCurrentUser() {
        return this.user;
    }
    
    // Check if user is authenticated
    isAuthenticated() {
        return !!this.user && !!this.username && !!this.password;
    }
    
    // Get headers with Basic authentication
    getAuthHeaders() {
        const headers = { ...defaultHeaders };
        if (this.username && this.password) {
            const credentials = btoa(`${this.username}:${this.password}`);
            headers['Authorization'] = `Basic ${credentials}`;
        }
        return headers;
    }
    
    // Store authentication data
    setAuth(username, password, user) {
        this.username = username;
        this.password = password;
        this.user = user;
        
        // Store user data in localStorage (persists across browser sessions)
        localStorage.setItem(USER_KEY, JSON.stringify(user));
        
        // Store credentials in sessionStorage (cleared when browser/tab is closed)
        sessionStorage.setItem(CREDENTIALS_KEY, JSON.stringify({
            username,
            password
        }));
    }
    
    // Clear authentication data
    clearAuth() {
        this.username = null;
        this.password = null;
        this.user = null;
        localStorage.removeItem(USER_KEY);
        sessionStorage.removeItem(CREDENTIALS_KEY);
    }
    
    // Login user
    async login(username, password) {
        const response = await fetch(buildApiUrl('/auth/login'), {
            method: 'POST',
            headers: defaultHeaders,
            body: JSON.stringify({ username, password })
        });

        
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.reason || `Login failed: ${response.statusText}`);
        }
        
        const user = await response.json();
        this.setAuth(username, password, user);
        return user;
        
    }
    
    // Register new user
    async register(userData) {
        try {
            const response = await fetch(buildApiUrl('/auth/register'), {
                method: 'POST',
                headers: defaultHeaders,
                body: JSON.stringify(userData)
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.reason || `Registration failed: ${response.statusText}`);
            }
            
            const user = await response.json();
            // For registration, we don't automatically log in the user
            // They need to login separately
            return user;
        } catch (error) {
            console.error('Registration error:', error);
            throw error;
        }
    }
    
    // Logout user
    async logout() {
        try {
            // Clear pending requests
            this.clearPendingRequests();
            // With Basic auth, we just clear local data
            // The server doesn't need to be notified
            this.clearAuth();
        } catch (error) {
            console.error('Logout error:', error);
            // Ensure logout happens even if there's an error
            this.clearAuth();
        }
    }
    
    // Get current user info
    async getCurrentUserInfo() {
        try {
            const response = await fetch(buildApiUrl('/auth/me'), {
                method: 'GET',
                headers: this.getAuthHeaders()
            });
            
            if (!response.ok) {
                throw new Error('Failed to get user info');
            }
            
            const user = await response.json();
            this.user = user;
            localStorage.setItem(USER_KEY, JSON.stringify(user));
            return user;
        } catch (error) {
            console.error('Get user info error:', error);
            throw error;
        }
    }
    
    // Validate current session
    async validateSession() {
        try {
            if (!this.isAuthenticated()) {
                return false;
            }
            
            const response = await fetch(buildApiUrl('/auth/me'), {
                method: 'GET',
                headers: this.getAuthHeaders()
            });
            
            return response.ok;
        } catch (error) {
            console.error('Session validation error:', error);
            return false;
        }
    }
    
    // Make authenticated request with automatic retry
    async authenticatedRequest(url, options = {}) {
        const headers = this.getAuthHeaders();
        const response = await fetch(url, {
            ...options,
            headers: {
                ...headers,
                ...options.headers
            }
        });
        
        if (response.status === 401) {
            // Authentication failed, queue the request and trigger re-authentication
            const requestPromise = new Promise((resolve, reject) => {
                pendingRequests.push({ url, options, resolve, reject });
            });
            
            // Clear current auth and trigger re-authentication
            this.clearAuth();
            
            // Dispatch event to trigger login modal
            window.dispatchEvent(new CustomEvent('authenticationRequired'));
            
            // Return the promise that will be resolved after re-authentication
            return requestPromise;
        }
        
        return response;
    }
    
    // Retry all pending requests after successful re-authentication
    async retryPendingRequests() {
        if (pendingRequests.length === 0) return;
        
        const requestsToRetry = [...pendingRequests];
        pendingRequests = []; // Clear the queue
        
        const results = await Promise.allSettled(
            requestsToRetry.map(async ({ url, options, resolve, reject }) => {
                try {
                    const response = await this.authenticatedRequest(url, options);
                    resolve(response);
                    return { success: true, response };
                } catch (error) {
                    reject(error);
                    return { success: false, error };
                }
            })
        );
        
        return results;
    }
    
    // Clear pending requests (useful for logout)
    clearPendingRequests() {
        pendingRequests.forEach(({ reject }) => {
            reject(new Error('Request cancelled due to logout'));
        });
        pendingRequests = [];
    }
}

// Create singleton instance
const authService = new AuthService();

// Export the service
export default authService;

// Export individual functions for convenience
export const {
    login,
    register,
    logout,
    getCurrentUserInfo,
    isAuthenticated,
    getCurrentUser,
    authenticatedRequest,
    retryPendingRequests,
    clearPendingRequests
} = authService;
